import { estimateAssetMemorySize, getAssetReferenceKey, getUniqueAssetReferences } from './AssetMemorySizeEstimator';
import { getXmlDocument, parseTargetMemoryRegions } from './AssetMemoryParsers';
import {
    createTargetMemoryInternals,
    createTargetReportInternals,
    finishAnalysis,
    getNoMemoryRegion,
    normalizeSizeEstimate,
    type ResolvedAssetMemorySizeEstimate,
    type TargetMemoryInternal,
    type TargetReportInternal
} from './AssetMemoryReportBuilder';
import { createAssetReference, resolveBlockTargetNames, validateMemoryForTargets } from './AssetMemoryResolver';
import type {
    AssetMemoryAnalysis,
    AssetMemoryAnalysisOptions,
    AssetMemoryAssetReference,
    AssetMemoryBlockName,
    AssetMemoryEstimateOptions,
    AssetMemoryReportAsset,
    AssetMemorySizeEstimate,
    AssetMemoryStorageIdentity,
    MemoryRegionReport
} from './AssetMemoryTypes';
export * from './AssetMemoryTypes';
export {
    estimateImageDataSizeBytes,
    getXmlDocument,
    parseBandwidthToBytesPerSecond,
    parseDataSizeToBytes,
    parseTargetMemoryRegions,
    readPngDimensions
} from './AssetMemoryParsers';
export { estimateAssetMemorySize, estimateGeneratedCArraySizeBytes } from './AssetMemorySizeEstimator';

const ASSET_TAGS_BY_BLOCK: Record<AssetMemoryBlockName, AssetMemoryAssetReference['tagName'][]> = {
    images: ['data', 'file'],
    fonts: ['tiny_ttf', 'bin']
};

// Using a non-printable character as a separator to minimize the risk of collisions with actual asset paths or names.
const REPORT_KEY_SEPARATOR: string = '\u0000';

const normalizeStoragePath = (sourcePath: string): string => sourcePath.replace(/\\/g, '/').replace(/^\/+/, '');

const getAssetStorageIdentity = (asset: AssetMemoryAssetReference): AssetMemoryStorageIdentity => {
    if (asset.sourcePath && asset.blockName === 'images' && asset.tagName === 'file') {
        const storagePath: string = normalizeStoragePath(asset.sourcePath);
        return {
            key: ['source-file', asset.blockName, asset.tagName, storagePath].join(REPORT_KEY_SEPARATOR),
            label: storagePath
        };
    }

    if (asset.sourcePath && asset.blockName === 'fonts' && asset.tagName === 'tiny_ttf') {
        const storagePath: string = normalizeStoragePath(asset.sourcePath);
        return {
            key: ['tiny-ttf-source', asset.blockName, asset.tagName, storagePath].join(REPORT_KEY_SEPARATOR),
            label: storagePath
        };
    }

    const fallbackLabel: string = asset.name || asset.sourcePath || 'Unnamed asset';
    return {
        key: [
            'generated-asset',
            asset.blockName,
            asset.tagName,
            asset.name,
            asset.sourcePath,
            asset.memoryName ?? '',
            asset.targetNames.join('|')
        ].join(REPORT_KEY_SEPARATOR),
        label: fallbackLabel
    };
};

const getAccessTimeMs = (sizeEstimate: ResolvedAssetMemorySizeEstimate, region: MemoryRegionReport): number | null => {
    if (sizeEstimate.status !== 'resolved' || !region.bandwidthBytesPerSecond) return null;
    return (sizeEstimate.sizeBytes / region.bandwidthBytesPerSecond) * 1000;
};

const shouldReplaceAssetSizeEstimate = (
    asset: AssetMemoryReportAsset,
    sizeEstimate: ResolvedAssetMemorySizeEstimate
): boolean => {
    if (asset.sizeStatus !== 'resolved' && sizeEstimate.status === 'resolved') return true;
    return asset.sizeStatus === 'unknown' && sizeEstimate.status === 'requires-code-export';
};

const updateAssetSizeEstimate = (
    asset: AssetMemoryReportAsset,
    sizeEstimate: ResolvedAssetMemorySizeEstimate,
    region: MemoryRegionReport
): void => {
    asset.sizeBytes = sizeEstimate.sizeBytes;
    asset.sizeStatus = sizeEstimate.status;
    asset.sizeSource = sizeEstimate.source;
    asset.sizeNote = sizeEstimate.note;
    asset.accessTimeMs = getAccessTimeMs(sizeEstimate, region);
};

/**
 * Builds the target memory usage report from project.xml and globals.xml.
 * Asset sizes come from the optional resolver; unresolved assets remain in the report with zero bytes and unknown status.
 */
export const analyzeAssetMemoryUsage = (
    projectXml: string | Document,
    globalsXml: string | Document,
    options: AssetMemoryAnalysisOptions = {}
): AssetMemoryAnalysis => {
    const { targets: parsedTargets, diagnostics } = parseTargetMemoryRegions(projectXml, options);
    const globalsDocument: Document | null = getXmlDocument(globalsXml, options.parseXml, diagnostics, 'globals.xml');
    const targetInternals: TargetMemoryInternal[] = createTargetMemoryInternals(parsedTargets);

    const targetInternalsByName: Map<string, TargetMemoryInternal> = new Map(
        targetInternals.map((target: TargetMemoryInternal) => [target.name, target])
    );

    const reportInternals: TargetReportInternal[] = createTargetReportInternals(targetInternals);

    const reportsByTargetName: Map<string, TargetReportInternal> = new Map(
        reportInternals.map((report: TargetReportInternal) => [report.targetName, report])
    );

    if (!globalsDocument) return finishAnalysis(reportInternals, parsedTargets, diagnostics);

    if (targetInternals.length === 0) {
        diagnostics.push({
            severity: 'error',
            code: 'no-project-targets',
            message: 'No targets were found in project.xml.'
        });
        return finishAnalysis(reportInternals, parsedTargets, diagnostics);
    }

    const assetBlocks: Element[] = Array.from(globalsDocument.documentElement?.children ?? []).filter(
        (child: Element) => ['images', 'fonts'].includes(child.tagName.toLowerCase())
    );

    for (const block of assetBlocks) {
        const blockName = block.tagName.toLowerCase() as AssetMemoryBlockName;
        const allowedAssetTags: Set<string> = new Set(ASSET_TAGS_BY_BLOCK[blockName]);
        const assets: Element[] = Array.from(block.children).filter((child: Element) =>
            allowedAssetTags.has(child.tagName.toLowerCase())
        );

        if (assets.length === 0) continue;

        const memoryName: string | null = block.getAttribute('memory')?.trim() || null;
        const targetNames: string[] | null = resolveBlockTargetNames(
            block,
            memoryName,
            targetInternalsByName,
            diagnostics
        );

        if (!targetNames) continue;
        if (!validateMemoryForTargets(blockName, memoryName, targetNames, targetInternalsByName, diagnostics)) continue;

        for (const assetElement of assets) {
            const assetReference: AssetMemoryAssetReference = createAssetReference(
                blockName,
                assetElement,
                memoryName,
                targetNames
            );
            const sizeEstimate: ResolvedAssetMemorySizeEstimate = normalizeSizeEstimate(
                options.resolveAssetSizeBytes?.(assetReference)
            );

            for (const targetName of targetNames) {
                const targetReport: TargetReportInternal | undefined = reportsByTargetName.get(targetName);
                if (!targetReport) continue;

                const region: MemoryRegionReport | undefined = memoryName
                    ? targetReport.regionsByName.get(memoryName)
                    : getNoMemoryRegion(targetReport);

                if (!region) continue;

                const storageIdentity: AssetMemoryStorageIdentity = getAssetStorageIdentity(assetReference);
                const assetId: string = `${targetName}${REPORT_KEY_SEPARATOR}${storageIdentity.key}`;
                const existingAsset: AssetMemoryReportAsset | undefined = region.assets.find(
                    (asset: AssetMemoryReportAsset) => asset.id === assetId
                );

                if (existingAsset) {
                    if (shouldReplaceAssetSizeEstimate(existingAsset, sizeEstimate)) {
                        updateAssetSizeEstimate(existingAsset, sizeEstimate, region);
                    }
                    continue;
                }

                region.assets.push({
                    ...assetReference,
                    id: assetId,
                    storageKey: storageIdentity.key,
                    storageLabel: storageIdentity.label,
                    targetName,
                    sizeBytes: sizeEstimate.sizeBytes,
                    sizeStatus: sizeEstimate.status,
                    sizeSource: sizeEstimate.source,
                    sizeNote: sizeEstimate.note,
                    accessTimeMs: getAccessTimeMs(sizeEstimate, region)
                });
            }
        }
    }

    return finishAnalysis(reportInternals, parsedTargets, diagnostics);
};

/**
 * Estimates asset sizes from project/generated files before running the analyzer.
 * This keeps the pure analyzer synchronous while providing a convenience path for callers with filesystem access.
 */
export const analyzeAssetMemoryUsageWithEstimates = async (
    projectXml: string | Document,
    globalsXml: string | Document,
    options: AssetMemoryEstimateOptions
): Promise<AssetMemoryAnalysis> => {
    const initialAnalysis: AssetMemoryAnalysis = analyzeAssetMemoryUsage(projectXml, globalsXml, options);
    const assetReferences: AssetMemoryAssetReference[] = getUniqueAssetReferences(initialAnalysis);
    const estimateByAssetKey: Map<string, AssetMemorySizeEstimate> = new Map();

    await Promise.all(
        assetReferences.map(async (asset: AssetMemoryAssetReference): Promise<void> => {
            estimateByAssetKey.set(getAssetReferenceKey(asset), await estimateAssetMemorySize(asset, options));
        })
    );

    return analyzeAssetMemoryUsage(projectXml, globalsXml, {
        ...options,
        resolveAssetSizeBytes: (asset: AssetMemoryAssetReference) => estimateByAssetKey.get(getAssetReferenceKey(asset))
    });
};
